'use client';
import { createContext, useEffect, useContext, useState, useMemo } from 'react';
import queryString from 'querystring';
import { useRouter, usePathname } from 'next/navigation';

import { TKeyValuePair } from '@/types';
import { ICompareResponse, ITabularCompare } from '@/lib/tabularCompareApi';
import { IClientWrapperFileProps, IClientWrapperCompareProps } from './types';
import { getDataStructure, hasEditableData } from './DataGrid/lib';
import { IPagination } from '@/types';
import { DataGridHandle } from 'react-data-grid';
import { ISchemaField } from '@/types';

import { SYSTEM_ROW_ID_FIELD_NAME } from './consts';

import useRemoteDataframe from '@/hooks/useRemoteDataframe';

// TODO: Mayve not keep null once I have a better understanding
const DataframeContext = createContext<any>({} as any);

export const DataframeContextProvider: React.FC<any & { children: React.ReactNode }> = ({
  repository,
  branch,
  path,
  fileRootPath,
  workspaceId,
  searchParams,
  mode,
  dataFrameResponse,
  compareResponse,
  children,
}) => {
  const compareValue: IClientWrapperCompareProps = {
    repository,
    mode,
    searchParams,
    dataFrameResponse,
    compareResponse: compareResponse as ICompareResponse | ITabularCompare,
  };

  const fileValue: IClientWrapperFileProps = {
    repository,
    mode,
    searchParams,
    dataFrameResponse,
    fileRootPath: fileRootPath as string,
  };

  const {
    isLoading,
    isValidating,
    data: remoteDataframeResponse,
    resultsDownloadUrl,
    error,
    addDfRow,
    removeDfRow,
    updateDfRow,
    restoreDfRow,
    addDfColumn,
    removeDfColumn,
    updateDfColumn,
    restoreDfColumn,
  } = useRemoteDataframe({
    repository,
    branch,
    path,
    query: searchParams,
    workspaceId,
    mode,
    initialData: dataFrameResponse,
  });

  // console.log('DataframeContext > mode', mode);

  const workingData = ['view', 'sample', 'compare', 'storybook_editable'].includes(mode)
    ? dataFrameResponse
    : remoteDataframeResponse;

  const data_frame = workingData?.data_frame;
  const dfQuery = data_frame?.query;
  const viewData = data_frame?.view;
  const sourceData = data_frame?.source;
  const pagination = viewData?.pagination as IPagination;

  const isEditableMode = ['remote_edit', 'remote_diff', 'storybook_editable'].includes(mode);
  const isEditableData = hasEditableData(viewData?.schema, sourceData?.schema);
  const isEditable = isEditableMode && isEditableData;

  const [rows, setRows] = useState<TKeyValuePair[]>(viewData?.data || []);
  const [addedRowId, setAddedRowId] = useState<string | null>(null);

  useEffect(() => {
    setRows(viewData?.data || []);
  }, [viewData?.data]);

  const dataStructure = useMemo(
    () =>
      viewData &&
      getDataStructure({
        dataFrame: viewData,
        compareResponse,
        fileRootPath,
        editable: isEditable,
      }),
    [viewData, compareResponse, fileRootPath, isEditable],
  );

  const [gridRef, setGridRef] = useState<React.RefObject<DataGridHandle>>();

  // TODO: Replace the spread operator with the actual values, then propery type this module
  const value = {
    ...(compareResponse ? compareValue : fileValue),
    viewData,
    sourceData,
    dfQuery,
    pagination,
    error,
    // fileInfo,
    workspaceId,
    mode,
    editable: isEditable,
    displayMode: isEditableMode ? 'edit' : 'view',
    rows,
    setRows,
    columns: dataStructure?.columns,
    addedRowId,
    setAddedRowId,
    updateDfRow,
    addDfRow,
    removeDfRow,
    restoreDfRow,
    addColumn: addDfColumn,
    removeColumn: removeDfColumn,
    updateColumn: updateDfColumn,
    restoreColumn: restoreDfColumn,
    isValidating,
    isLoading: isLoading || isValidating,
    resultsDownloadUrl,
    gridRef,
    setGridRef,
  };

  return <DataframeContext.Provider value={value}>{children}</DataframeContext.Provider>;
};

const useDataframeContext = () => {
  const context = useContext(DataframeContext);
  const {
    repository,
    viewData,
    sourceData,
    dfQuery,
    searchParams,
    workspaceId,
    pagination,
    fileRootPath,
    compareResponse,
    editable,
    displayMode,
    error,
    mode,
    rows,
    setRows,
    columns,
    addedRowId,
    setAddedRowId,
    updateDfRow,
    addDfRow,
    removeDfRow,
    restoreDfRow,
    addColumn,
    removeColumn,
    updateColumn,
    restoreColumn,
    isValidating,
    isLoading,
    resultsDownloadUrl,
    gridRef,
    setGridRef,
  } = context;

  const router = useRouter();
  const currentPathname = usePathname();

  const updateRow = async (changedRow: TKeyValuePair): Promise<boolean> => {
    const resp = await updateDfRow(changedRow);

    const pendingRow = { ...changedRow };
    pendingRow[SYSTEM_ROW_ID_FIELD_NAME] = 'dirty';
    const updatedRows = rows.map((row: TKeyValuePair) => {
      if (changedRow[SYSTEM_ROW_ID_FIELD_NAME] === row[SYSTEM_ROW_ID_FIELD_NAME]) {
        return pendingRow;
      }
      return row;
    });
    setRows(updatedRows);
    return resp;
  };

  const deleteRow = async (rowId: string) => {
    const response = await removeDfRow(rowId);
    if (response) {
      const updatedRows = rows.filter((row: TKeyValuePair) => row[SYSTEM_ROW_ID_FIELD_NAME] !== rowId);
      setRows(updatedRows);
      return true;
    }
    return false;
  };

  const addBlankRow = async () => {
    const newRow = await addDfRow();
    setAddedRowId(newRow[SYSTEM_ROW_ID_FIELD_NAME]);

    const tmpSearch = { ...searchParams };
    tmpSearch.page = pagination.total_pages;

    const query = queryString.stringify(tmpSearch);
    router.push(`${currentPathname}?${query}`);
  };

  return {
    repository,
    viewData,
    sourceData,
    dfQuery,
    searchParams,
    workspaceId,
    pagination,
    fileRootPath,
    compareResponse, //  required to render the compare schema
    columns,
    error,
    rows,
    mode,
    setRows,
    addedRowId,
    setAddedRowId,
    updateRow,
    editable,
    displayMode,
    addBlankRow,
    deleteRow,
    restoreDfRow,
    addColumn: addColumn as (column: ISchemaField) => Promise<boolean>,
    removeColumn: removeColumn as (columnName: string) => Promise<boolean>,
    updateColumn: updateColumn as (
      columnName: string,
      updatedColumn: ISchemaField,
      renderFunc: string,
      codeLanguage?: string,
    ) => Promise<boolean>,
    restoreColumn,
    isValidating,
    isLoading,
    resultsDownloadUrl,
    gridRef,
    setGridRef,
  };
};

export default useDataframeContext;
